import {
    getBlockedFormExplanation,
    partitionFormsToDo,
    type FormsAuthCtx,
} from "@/features/diaries/features/new/hooks/useFormsToDo";
import { resolveActionableTrackerDueEntry } from "@/features/diaries/utils/findEarliestDueEntry";
import type {
    ClinicianWorklistDiary,
    IncompleteDataDiary,
    IncompleteDetailsTarget,
    WorklistConsent,
    WorklistProcedureKind,
} from "../api/worklistApi";
import {
    consentWorklistItemId,
    eventDetailsWorklistItemId,
    formWorklistItemId,
    resolveWorklistFormSlot,
} from "./worklistFormSlot";

export type WorklistItemKind = "form" | "event_details" | "consent";
export type WorklistItemStatus = "due" | "blocked" | "incomplete";
export type WorklistItemAction = "complete" | "complete_details" | "sign_consent" | "open_diary" | "restore";

export interface WorklistItem {
    id: string;
    kind: WorklistItemKind;
    /** Form name (e.g. WOMAC) or procedure details label (e.g. Injection details). */
    task: string;
    patientName: string;
    hospitalNumber: string | null;
    organisationName: string | null;
    diaryId: string;
    trackerId: string | null;
    /** Form schedule slot identity for archive matching. */
    scheduleId: string | null;
    scheduleIndex: number | null;
    status: WorklistItemStatus;
    dueAt: string | null;
    blockedBy: string | null;
    action: WorklistItemAction;
    /** Deep link into intervention-details when completing event details. */
    detailsTarget: IncompleteDetailsTarget | null;
    /** Unsigned clinician consent to sign from the worklist. */
    consentId: string | null;
    /** Present when the row is shown from a personal archive. */
    dismissalId?: string;
    dismissedAt?: string | null;
}

export interface BuildWorklistItemsResult {
    items: WorklistItem[];
    formTaskCount: number;
    incompleteDataCount: number;
    consentTaskCount: number;
}

export interface WorklistProcedureDetailsLabels {
    injection: string;
    surgery: string;
    shockwave: string;
    procedure: string;
}

function patientNameFrom(patientInfo: { patient_name: string | null } | null | undefined, fallback: string): string {
    return patientInfo?.patient_name?.trim() || fallback;
}

function byPatientThenTask(a: WorklistItem, b: WorklistItem): number {
    const nameDiff = a.patientName.localeCompare(b.patientName);
    if (nameDiff !== 0) return nameDiff;
    const statusOrder: Record<WorklistItemStatus, number> = { due: 0, incomplete: 1, blocked: 2 };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    return a.task.localeCompare(b.task);
}

export function procedureDetailsTaskLabel(kind: WorklistProcedureKind, labels: WorklistProcedureDetailsLabels): string {
    return labels[kind];
}

/** Merge clinician form tasks, consents, and incomplete-event diaries into one worklist. */
export function buildWorklistItems(options: {
    formDiaries: ClinicianWorklistDiary[] | null | undefined;
    incompleteDiaries: IncompleteDataDiary[] | null | undefined;
    consents?: WorklistConsent[] | null;
    auth: FormsAuthCtx;
    unknownPatient?: string;
    consentFallback?: string;
    procedureDetailsLabels?: WorklistProcedureDetailsLabels;
}): BuildWorklistItemsResult {
    const {
        formDiaries,
        incompleteDiaries,
        consents,
        auth,
        unknownPatient = "Unknown patient",
        consentFallback = "Consent",
        procedureDetailsLabels = {
            injection: "Injection details",
            surgery: "Surgery details",
            shockwave: "Shockwave details",
            procedure: "Procedure details",
        },
    } = options;

    const items: WorklistItem[] = [];

    for (const diary of formDiaries ?? []) {
        const partitioned = partitionFormsToDo(diary.trackers_view, auth);
        const patientName = patientNameFrom(diary.patient_info, unknownPatient);

        for (const item of partitioned.toDo) {
            const dueEntry = resolveActionableTrackerDueEntry(item.data);
            const slot = resolveWorklistFormSlot(item.data);
            if (!slot) continue;
            items.push({
                id: formWorklistItemId(diary.diary_id, item.data.tracker_id, slot),
                kind: "form",
                task: item.name,
                patientName,
                hospitalNumber: diary.hospital_number,
                organisationName: diary.organisation_name,
                diaryId: diary.diary_id,
                trackerId: item.data.tracker_id,
                scheduleId: slot.scheduleId,
                scheduleIndex: slot.scheduleIndex,
                status: "due",
                dueAt: dueEntry?.due_at ?? item.data.due_entries?.[0]?.due_at ?? null,
                blockedBy: null,
                action: "complete",
                detailsTarget: null,
                consentId: null,
            });
        }

        for (const item of partitioned.blocked) {
            const slot = resolveWorklistFormSlot(item.data);
            if (!slot) continue;
            items.push({
                id: formWorklistItemId(diary.diary_id, item.data.tracker_id, slot),
                kind: "form",
                task: item.name,
                patientName,
                hospitalNumber: diary.hospital_number,
                organisationName: diary.organisation_name,
                diaryId: diary.diary_id,
                trackerId: item.data.tracker_id,
                scheduleId: slot.scheduleId,
                scheduleIndex: slot.scheduleIndex,
                status: "blocked",
                dueAt: item.data.due_entries?.[0]?.due_at ?? item.data.upcoming_entries?.[0]?.due_at ?? null,
                blockedBy: getBlockedFormExplanation(item),
                action: "open_diary",
                detailsTarget: null,
                consentId: null,
            });
        }
    }

    for (const diary of incompleteDiaries ?? []) {
        items.push({
            id: eventDetailsWorklistItemId(diary.diary_id, diary.detailsTarget?.eventId),
            kind: "event_details",
            task: procedureDetailsTaskLabel(diary.procedureKind, procedureDetailsLabels),
            patientName: patientNameFrom(diary.patient_info, unknownPatient),
            hospitalNumber: diary.hospital_number,
            organisationName: diary.organisation_name,
            diaryId: diary.diary_id,
            trackerId: diary.detailsTarget?.trackerId ?? null,
            scheduleId: null,
            scheduleIndex: null,
            status: "incomplete",
            dueAt: null,
            blockedBy: null,
            action: "complete_details",
            detailsTarget: diary.detailsTarget,
            consentId: null,
        });
    }

    for (const consent of consents ?? []) {
        items.push({
            id: consentWorklistItemId(consent.diary_id, consent.consent_id),
            kind: "consent",
            task: consent.consent_name?.trim() || consentFallback,
            patientName: patientNameFrom(consent.patient_info, unknownPatient),
            hospitalNumber: consent.hospital_number,
            organisationName: consent.organisation_name,
            diaryId: consent.diary_id,
            trackerId: null,
            scheduleId: null,
            scheduleIndex: null,
            status: "due",
            dueAt: null,
            blockedBy: null,
            action: "sign_consent",
            detailsTarget: null,
            consentId: consent.consent_id,
        });
    }

    const formTaskCount = items.filter((item) => item.kind === "form").length;
    const incompleteDataCount = items.filter((item) => item.kind === "event_details").length;
    const consentTaskCount = items.filter((item) => item.kind === "consent").length;

    return {
        items: items.toSorted(byPatientThenTask),
        formTaskCount,
        incompleteDataCount,
        consentTaskCount,
    };
}

export function countWorklistBadge(formTaskCount: number, incompleteDataCount: number, consentTaskCount = 0): number {
    return Math.max(0, formTaskCount) + Math.max(0, incompleteDataCount) + Math.max(0, consentTaskCount);
}
